import * as moment from 'moment';
import * as _ from 'lodash';
import {IFormController, INgModelController} from 'angular';
import {Set} from 'immutable';
import {
    IStation,
    IJournalData,
    IDailyTriplet,
    IDischargeTuple,
    IDataValue, IDecadalJournalData
} from '../../api/api.types';
import {IDataValueApi} from '../../api/data-value-api.service';
import {IIcePhenomena} from '../input/kn15-telegram/kn15-parse.service';

export interface IJournalDailyDataRow {
  date: moment.Moment;
  waterLevelMorning?: number;
  waterLevelTrend?: number;
  dischargeMorning?: number;
  waterLevelEvening?: number;
  dischargeEvening?: number;
  waterLevelAverage?: number;
  dischargeAverage?: number;
  waterTemperature?: number;
  airTemperature?: number;
  icePhenomenae?: IIcePhenomena[];
}

export interface IJournalDischargeDataRow {
  date: moment.Moment;
  waterLevel: IDataValue;
  discharge: IDataValue;
  freeRiverArea?: IDataValue;
}

export interface IJournalDecadalDataRow {
  decadeInMonth: number;
  waterLevel?: number;
  discharge?: number;
  precipitation?: number;
  temperature?: number;
}

export interface IJournalStationView {
  station: IStation;
  dailyData: IJournalDailyDataRow[];
  dischargeData: IJournalDischargeDataRow[];
  decadalData: IJournalDecadalDataRow[];
}

interface IDateSelectorForm extends IFormController {
  year: INgModelController;
  month: INgModelController;
}

export default class OperationalJournalController {
  private static readonly $inject = ['$scope', 'dataValueApi', '$rootScope'];
  private static readonly YEAR_RANGE_SIZE = 10;
  private static readonly JOURNAL_YEARS: number[] = _.range(
      moment().year() - OperationalJournalController.YEAR_RANGE_SIZE,
      moment().year() + OperationalJournalController.YEAR_RANGE_SIZE);

  /**
   * Stations selected for journal generation.
   */
  selectedStations: Set<IStation>;
  /**
   * Date for the journal.
   */
  selectedDate: moment.Moment;
  /**
   * Selected month model.
   */
  selectedMonth: number;
  /**
   * Selected year model.
   */
  selectedYear: number;
  /**
   * Whether a loading operation is in progress.
   */
  loadingInProgress: boolean;
  /**
   * Displayed journal data.
   */
  journalViewData: IJournalStationView[];
  /**
   * Localized month names.
   */
  months: string[];
  /**
   * Hard-coded years available for selection.
   */
  readonly years: number[];
  /**
   * Form controller containing the overriddenDate selectors.
   */
  dateForm: IDateSelectorForm;
  /**
   * Index of the selected station tab.
   */
  selectedTabIndex: number;
  /**
   * Highlighted station, i.e. selected tab.
   */
  highlightedStation: IStation;
  /**
   * De-registration function for the language change listener.
   */
  private readonly deregisterTranslateListener: () => void;

  private loadedJournalData: IJournalData;

  constructor(private $scope: ng.IScope, private dataValueApi: IDataValueApi,
              private $rootScope: ng.IRootScopeService) {
    this.selectedStations = Set<IStation>();
    this.selectedDate = moment().startOf('month');
    this.selectedMonth = this.selectedDate.month();
    this.selectedYear = this.selectedDate.year();
    this.loadingInProgress = false;
    this.months = moment.months();
    this.years = OperationalJournalController.JOURNAL_YEARS;
    this.journalViewData = [];
    this.deregisterTranslateListener = this.$rootScope
        .$on('$translateChangeSuccess', () => {
          this.months = moment.months();
          this.selectedDate = moment()
              .month(this.selectedDate.month())
              .year(this.selectedDate.year());
        });
  }

  // ===========================================================================
  // Lifecycle hooks
  // ===========================================================================
  $postLink() {
    // Needed because of md-select not registering a default value and
    // triggering the validation.
    this.selectedMonth = this.selectedDate.month();
    this.selectedYear = this.selectedDate.year();
    this.$scope.$applyAsync(() => {
      this.dateForm.month.$validate();
      this.dateForm.year.$validate();
    });
  }

  $onDestroy() {
    this.deregisterTranslateListener();
  }

  get disabledLoadButton(): boolean {
    return !this.dateForm || this.dateForm.$invalid
        || this.selectedStations.isEmpty();
  }

  onSelectedStationsChange(selectedStations: Set<IStation>) {
    this.selectedStations = selectedStations;
  }

  onHighlightedStationChange(station: IStation|null) {
    this.determineTabIndex(station);
  }

  onTabSelect(journalViewDatum: IJournalStationView) {
    if (this.loadingInProgress) {
      // Ignore selection.
      return;
    }
    this.highlightedStation = journalViewDatum.station;
  }

  loadJournal() {
    this.selectedDate = moment()
        .month(this.selectedMonth)
        .year(this.selectedYear);
    this.loadingInProgress = true;
    this.journalViewData = [];
    this.dataValueApi.getJournalData(this.selectedMonth, this.selectedYear,
        this.selectedStations.map(station => station.id).toArray())
        .then(journalData => {
          this.loadedJournalData = journalData;
          this.journalViewData = [];
          for (let siteId in this.loadedJournalData.siteData) {
            const siteDatum = this.loadedJournalData.siteData[siteId];
            
            this.journalViewData.push({
              station: siteDatum.site,
              dailyData: siteDatum.dailyData ? siteDatum.dailyData.map(this.toJournalDailyDataRow) : null,
              dischargeData: siteDatum.dischargeData ? siteDatum.dischargeData.map(
                  this.toJournalDischargeDataRow) : null,
              decadalData: siteDatum.decadalData.map(this.toJournalDecadalDataRow)
            });

            // removing the first item since it has date from previous month,
            // this item is used to calculate the waterlevel trend for the first day of a month
            this.journalViewData.forEach((journalViewDatum) => {
              if (journalViewDatum && journalViewDatum.dailyData && journalViewDatum.dailyData.length) {
                if (journalViewDatum.dailyData[0].date.month() !== this.selectedDate.month()) {
                  journalViewDatum.dailyData.splice(0,1);  
                }
              }
            })
            

          }
          this.journalViewData = this.journalViewData.sort((a, b) =>
              a.station.siteCode.localeCompare(b.station.siteCode));
          this.determineTabIndex(this.highlightedStation);
        })
        .finally(() => {
          this.loadingInProgress = false;
        });
  }

  private determineTabIndex(station: IStation | null) {
    if (!station) {
      return;
    }
    const foundIndex = this.journalViewData.findIndex(
        journalViewDatum => journalViewDatum.station.id == station.id);
    if (foundIndex > -1) {
      this.selectedTabIndex = foundIndex;
    } else {
      this.selectedTabIndex = 0;
      if (this.journalViewData.length) {
        this.highlightedStation = this.journalViewData[0].station;
      } else {
        this.highlightedStation = null;
      }
    }
  }

  private toJournalDailyDataRow(dailyTriplet: IDailyTriplet, index: number,
                                 dailyData: IDailyTriplet[]): IJournalDailyDataRow {

    let date: moment.Moment;
    if (dailyTriplet.eightData && dailyTriplet.eightData.waterLevel) {
      date = moment.unix(dailyTriplet.eightData.waterLevel.localDateTime).utc();
    } else if (dailyTriplet.twentyData && dailyTriplet.twentyData.waterLevel) {
      date = moment.unix(dailyTriplet.twentyData.waterLevel.localDateTime).utc();
    } else if (dailyTriplet.eightData && dailyTriplet.eightData.discharge) {
      date = moment.unix(dailyTriplet.eightData.discharge.localDateTime).utc();
    } else if (dailyTriplet.twentyData && dailyTriplet.twentyData.discharge) {
      date = moment.unix(dailyTriplet.twentyData.discharge.localDateTime).utc();
    } else if (dailyTriplet.averageData && dailyTriplet.averageData.discharge) {
      date = moment.unix(dailyTriplet.averageData.discharge.localDateTime).utc();
    }

    const waterLevelMorning = dailyTriplet.eightData &&
        dailyTriplet.eightData.waterLevel &&
        dailyTriplet.eightData.waterLevel.dataValue;
    const dischargeMorning = dailyTriplet.eightData &&
        dailyTriplet.eightData.discharge &&
        dailyTriplet.eightData.discharge.dataValue;
    let waterLevelTrend: number; 
    if (dailyTriplet.eightData && dailyTriplet.eightData.waterLevel && dailyData[index - 1] &&
        dailyData[index - 1].eightData) {
      waterLevelTrend = dailyTriplet.eightData.waterLevel.dataValue -
          dailyData[index - 1].eightData.waterLevel.dataValue;
    }

    const waterLevelEvening = dailyTriplet.twentyData &&
        dailyTriplet.twentyData.waterLevel &&
        dailyTriplet.twentyData.waterLevel.dataValue;
    const dischargeEvening = dailyTriplet.twentyData &&
        dailyTriplet.twentyData.discharge &&
        dailyTriplet.twentyData.discharge.dataValue;

    // set averages only if both morning and evening values are present
    const waterLevelAverage = dailyTriplet.averageData && waterLevelMorning && waterLevelEvening &&
        dailyTriplet.averageData.waterLevel.dataValue;
    let dischargeAverage = dailyTriplet.averageData && dischargeMorning && dischargeEvening &&
        dailyTriplet.averageData.discharge.dataValue;

    if (!dailyTriplet.eightData && !dailyTriplet.twentyData) {
      dischargeAverage = dailyTriplet.averageData.discharge.dataValue;
    }

    const waterTemperature = dailyTriplet.additionalData &&
        dailyTriplet.additionalData.waterTemperature &&
            dailyTriplet.additionalData.waterTemperature.dataValue;
    const airTemperature = dailyTriplet.additionalData &&
        dailyTriplet.additionalData.airTemperature &&
        dailyTriplet.additionalData.airTemperature.dataValue;
    const icePhenomenae = dailyTriplet.additionalData &&
        dailyTriplet.additionalData.icePhenomenae;


    return {
      date,
      waterLevelMorning,
      waterLevelTrend,
      dischargeMorning,
      waterLevelEvening,
      dischargeEvening,
      waterLevelAverage,
      dischargeAverage,
      waterTemperature,
      airTemperature,
      icePhenomenae
    };
  }

  private toJournalDischargeDataRow(dischargeTuple: IDischargeTuple): IJournalDischargeDataRow {
    return {
      date: moment.unix(dischargeTuple.waterLevel.localDateTime).utc(),
      waterLevel: dischargeTuple.waterLevel,
      discharge: dischargeTuple.discharge,
      freeRiverArea: dischargeTuple.freeRiverArea
    };
  }

  private toJournalDecadalDataRow(decadalJournalDatum: IDecadalJournalData): IJournalDecadalDataRow {
    if (typeof decadalJournalDatum.averageData.precipitation !== 'undefined' ||
      typeof decadalJournalDatum.averageData.temperature !== 'undefined') {
      return {
        decadeInMonth: decadalJournalDatum.decadeInMonth,
        precipitation: decadalJournalDatum.averageData.precipitation && decadalJournalDatum.averageData.precipitation.dataValue,
        temperature: decadalJournalDatum.averageData.temperature && decadalJournalDatum.averageData.temperature.dataValue
      };
    } else {
      return {
        decadeInMonth: decadalJournalDatum.decadeInMonth,
        waterLevel: decadalJournalDatum.averageData.waterLevel,
        discharge: decadalJournalDatum.averageData.discharge
      };
    }
  }

}




/** WEBPACK FOOTER **
 ** ./app/operational/journal/journal.controller.ts
 **/